import { useSite, useSites } from "@inrange/building-manager-api-client";
import {
  Building,
  ChangeToSiteDefaults,
  RecursivePartial,
  RecursivePartialSiteAllowStringValues,
  Site,
  SiteAllowStringValues,
  SiteChangeLog,
  SiteFile,
  SiteOwnership,
} from "@inrange/building-manager-api-client/models-site";
import { SiteCalculationsProvider } from "@inrange/shared-components";
import { isSiteOwnerOccupied } from "@inrange/theme-components";
import deepmerge from "deepmerge";
import cloneDeep from "lodash/cloneDeep";
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useBlocker, useNavigate, useParams } from "react-router-dom";
import { UserContext } from "../../auth/UserContext";
import Loading from "../Loading";
import { applySiteUpdate, overwriteMerge } from "./site-utils";
import SiteEditView from "./SiteEditView";
import { ChangelogModal } from "./siteSections/ChangelogModal";
import { getTodayInTimeZone } from "./siteSections/utils";

type BuildingWithKey = Building & {
  key: string;
  surface_area_sqm: number;
  customised?: boolean;
  sourcePolygonType?: string;
  sourceGeometry?: any;
};

type OwnershipInput = {
  ownershipIndex: number;
  selectedID: string;
  selectedName: string;
  selectedType: string;
  selectedPark?: string;
};

const SiteEdit = () => {
  const { siteId } = useParams<{ siteId: string }>();
  const { user } = useContext(UserContext);
  const navigate = useNavigate();

  const {
    fetchSiteValues,
    LOSS_FACTORS_BY_KEY,
    fetchSite,
    updateSite,
    deleteSite,
    previewSiteLog,
  } = useSite({
    siteId,
    app: "admin",
  });

  const otherSiteIds: string[] = useMemo(() => {
    return [
      ...(fetchSite.data?.site?.lockedSiteID
        ? [fetchSite.data.site.lockedSiteID]
        : []),
      ...(fetchSite.data?.site?.unlockedSiteID
        ? [fetchSite.data.site.unlockedSiteID]
        : []),
    ];
  }, [
    fetchSite.data?.site?.lockedSiteID,
    fetchSite.data?.site?.unlockedSiteID,
  ]);
  const otherSitesQueries = useSites(otherSiteIds, undefined);

  // Create a stable key from the query data to avoid re-renders from useQueries returning new array references
  const otherSitesDataKey = otherSitesQueries
    .map((q) =>
      q.data ? JSON.stringify(q.data.sites.map((site) => site.id)) : ""
    )
    .join(",");

  // Memoize the flattened sites data
  const otherSitesById = useMemo(() => {
    return otherSitesQueries
      .flatMap((siteResult) => (siteResult.data ? siteResult.data.sites : []))
      .reduce(
        (acc, site) => {
          acc[site.id] = site;
          return acc;
        },
        {} as { [key: string]: Site }
      );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherSitesDataKey]);

  // siteUpdates contains only the attributes which have been updated
  const [siteUpdates, setSiteUpdates] =
    useState<RecursivePartialSiteAllowStringValues>({});
  const [pendingUpdateSite, setPendingUpdateSite] = useState<
    ChangeToSiteDefaults[] | undefined
  >(undefined);
  // previewSite contains a complete view of the site including updated attributes
  const [previewSite, setPreviewSite] = useState<SiteAllowStringValues>(
    {} as SiteAllowStringValues
  );

  // Save related state
  const [saveWarnings, setSaveWarnings] = useState<string[] | undefined>(
    undefined
  );
  const [withExtraUpdatesFromModal, setWithExtraUpdatesFromModal] = useState<
    | { site?: RecursivePartialSiteAllowStringValues; [key: string]: any }
    | undefined
  >(undefined);
  const [saveDisabled, setSaveDisabled] = useState(false);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [hasUnsavedBuildings, setHasUnsavedBuildings] = useState(false);
  const [alertStyle, setAlertStyle] = useState<string>("secondary");
  const [siteStatusMessage, setSiteStatusMessage] = useState<
    ReactNode | string | undefined
  >(undefined);
  const [alertMessage, setAlertMessage] = useState<
    ReactNode | string | undefined
  >(undefined);

  // Site bools
  const [isOwnerOccupied, setIsOwnerOccupied] = useState(false);

  // Site attribute trackers
  const [addedFiles, setAddedFiles] = useState<SiteFile[]>([]);
  const [deletedFiles, setDeletedFiles] = useState<SiteFile[]>([]);
  const [siteFiles, setSiteFiles] = useState<SiteFile[]>([]);
  const [newBuildings, setNewBuildings] = useState<BuildingWithKey[]>([]);
  const [originalBuildings, setOriginalBuildings] = useState<Building[]>([]);
  const [deletedOwnerships, setDeletedOwnerships] = useState<SiteOwnership[]>(
    []
  );

  // PV Design state
  const [pvDesignInstalledCapacity, setPvDesignInstalledCapacity] = useState<
    number | undefined
  >(undefined);

  // NOTE: newOwnerships is a weird structure - it's an object with integer index keys,
  // so it's *almost* an array, but not quite
  const [newOwnerships, setNewOwnerships] = useState<
    Record<number, SiteOwnership>
  >({});
  const [editedOwnerships, onOwnerEdited] = useState<
    Record<string, SiteOwnership>
  >({});

  // Changelog preview state
  const [showChangelogModal, setShowChangelogModal] = useState(false);
  const [changelogPreview, setChangelogPreview] =
    useState<SiteChangeLog | null>(null);

  const pvDesignFileUploadRef = useRef<HTMLInputElement>(null);

  // useCallback ensures this function value is consistent and hence can be used in a useEffect dependency array with no issues
  const setSiteState = useCallback(
    (update: RecursivePartialSiteAllowStringValues) => {
      setSiteUpdates((prevSite) =>
        deepmerge(prevSite, update, {
          arrayMerge: overwriteMerge,
        })
      );
      setPreviewSite((prevSite) => applySiteUpdate(prevSite, update));
    },
    [setSiteUpdates, setPreviewSite]
  );

  // Once site data is loaded, populate: previewSite, newBuildings, originalBuildings
  useEffect(() => {
    if (fetchSite.data?.site && Object.keys(previewSite).length === 0) {
      const site = fetchSite.data?.site;
      setPreviewSite(cloneDeep(site) as SiteAllowStringValues);
      const transformedBuildings: BuildingWithKey[] = [];
      site.buildings.forEach((b) => {
        transformedBuildings.push({
          key: b.id,
          surface_area_sqm: b.surfaceAreaSqM,
          ...b,
        });
      });
      setNewBuildings(transformedBuildings);
      setOriginalBuildings([...site.buildings]);
      setIsOwnerOccupied(isSiteOwnerOccupied(site.siteOwnerships));
      setSiteFiles(site.siteFiles);
    }
  }, [fetchSite.data?.site, previewSite]);

  // site is the original site data from the API
  const site = fetchSite.data?.site;

  useEffect(() => {
    if (!site) {
      return;
    }
    const reactNodes: ReactNode[] = [];
    if (site.locked) {
      reactNodes.push(
        <React.Fragment key="locked-status">
          <b>Site locked</b>
          {site.unlockedSiteID && otherSitesById[site.unlockedSiteID] && (
            <>
              {" "}
              - Unlocked version:{" "}
              <a
                href={`/site/${site.unlockedSiteID}/edit`}
                data-testid="unlocked-site-link"
              >
                {otherSitesById[site.unlockedSiteID].name}
              </a>
            </>
          )}
        </React.Fragment>
      );
    }
    if (
      !site.locked &&
      site.lockedSiteID &&
      otherSitesById[site.lockedSiteID]
    ) {
      reactNodes.push(
        <React.Fragment key="unlocked-status">
          <b>Unlocked version of locked site:</b>{" "}
          <a
            href={`/site/${site.lockedSiteID}/edit`}
            data-testid="locked-site-link"
          >
            {otherSitesById[site.lockedSiteID].name}
          </a>
        </React.Fragment>
      );
    }
    setSiteStatusMessage(reactNodes.length > 0 ? reactNodes : undefined);
  }, [otherSitesById, site]);

  const navigationBlocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      Object.keys(siteUpdates).length > 0 &&
      currentLocation.pathname !== nextLocation.pathname
  );

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (Object.keys(siteUpdates).length > 0 && !saveInProgress) {
        const message =
          "You have unsaved changes. Are you sure you want to leave?";
        event.preventDefault();
        event.returnValue = message;
        return message;
      }
    },
    [siteUpdates, saveInProgress]
  );

  useEffect(() => {
    if (!saveInProgress && Object.keys(siteUpdates).length > 0) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [handleBeforeUnload, saveInProgress, siteUpdates]);

  const setBuildingsForPreview = useCallback(
    (buildings: BuildingWithKey[]) => {
      const previewBuildings = buildings.map((b) => {
        return {
          id: b.key,
          surfaceAreaSqM: b.surface_area_sqm,
          siteID: siteId,
          geometry: b.geometry,
          updatedAt: Math.floor(Date.now() / 1000),
          polygonType: b.polygonType,
          sourcePolygonType: b.sourcePolygonType,
          sourceGeometry: b.sourceGeometry,
          createdBy: b.createdBy || "",
          createdAt: b.createdAt || 0,
        };
      });
      setPreviewSite((prevPreviewSite) => ({
        ...prevPreviewSite,
        buildings: previewBuildings as Building[],
      }));
      setNewBuildings(buildings);
    },
    [siteId]
  );

  if (
    (!fetchSite.isLoading && fetchSite.isError) ||
    (!fetchSiteValues.isLoading && fetchSiteValues.isError)
  ) {
    return <div>Error loading the site.</div>;
  }

  if (
    fetchSite.isLoading ||
    Object.keys(previewSite).length === 0 ||
    fetchSiteValues.isLoading ||
    !fetchSiteValues.data
  ) {
    return <Loading label="Loading site data..." />;
  }

  if (!site) {
    return <Loading label="Loading site data..." />;
  }

  const onOwnerAdded = ({
    ownershipIndex,
    selectedID,
    selectedName,
    selectedType,
    selectedPark,
  }: OwnershipInput) => {
    if (selectedType === "" || !selectedID) return;
    const updatedNewOwnerships = {
      ...newOwnerships,
      [ownershipIndex]: {
        orgID: selectedID,
        name: selectedName,
        ownership: selectedType,
        ...(selectedPark ? { park: selectedPark } : {}),
      },
    };
    setNewOwnerships(updatedNewOwnerships);

    // Add to preview site
    const updatedSiteOwnerships = Object.values(updatedNewOwnerships)
      .map((ownership) => ({
        orgID: ownership.orgID,
        name: ownership.name,
        ownership: ownership.ownership,
        siteID: siteId,
        ...(ownership.park ? { park: ownership.park } : {}),
      }))
      .sort((a, b) => `${a.orgID}`.localeCompare(`${b.orgID}`));
    setIsOwnerOccupied(isSiteOwnerOccupied(updatedSiteOwnerships));
    setPreviewSite((prevPreviewSite) => ({
      ...prevPreviewSite,
      siteOwnerships: updatedSiteOwnerships,
    }));
  };

  const onOwnerRemoved = (ownership: SiteOwnership) => {
    // We don't support removing added but unsaved ownerships
    onOwnerEdited((prevEditedOwnerships) => {
      const updatedEditedOwnerships = { ...prevEditedOwnerships };
      delete updatedEditedOwnerships[ownership.orgID];
      return updatedEditedOwnerships;
    });
    setDeletedOwnerships([...deletedOwnerships, ownership]);
    // Remove from preview site
    const updatedOwnerships = previewSite.siteOwnerships?.filter(
      (o) => o.orgID !== ownership.orgID
    );
    setIsOwnerOccupied(isSiteOwnerOccupied(updatedOwnerships || []));
    setPreviewSite((prevPreviewSite) => ({
      ...prevPreviewSite,
      siteOwnerships: updatedOwnerships,
    }));
  };

  const updateSiteFiles = (
    updatedAddedFiles: SiteFile[],
    updatedDeletedFiles: SiteFile[]
  ) => {
    const siteFilesWithoutDeletionsAndAdditions = [...site.siteFiles].filter(
      (file) =>
        !updatedDeletedFiles.some((f) => f.fileGuid === file.fileGuid) &&
        !updatedAddedFiles.some((f) => f.fileGuid === file.fileGuid)
    );
    const updatedSiteFiles = [
      ...siteFilesWithoutDeletionsAndAdditions,
      ...updatedAddedFiles,
    ];

    setSiteFiles(updatedSiteFiles);
  };

  const onFileAdded = (siteFile: SiteFile) => {
    if (siteFile.fileType === "pvDesignImage") {
      const addedFilesWithoutPvDesignImage = addedFiles.filter(
        (f) => f.fileType !== "pvDesignImage"
      );
      const newAddedFiles = [...addedFilesWithoutPvDesignImage, siteFile];
      setAddedFiles(newAddedFiles);
      const oldPvDesign = siteFiles.find((f) => f.fileType === "pvDesignImage");

      const newDeletedFiles = oldPvDesign
        ? [...deletedFiles, oldPvDesign]
        : deletedFiles;
      if (oldPvDesign?.fileVersionId) {
        setDeletedFiles(newDeletedFiles);
      }
      updateSiteFiles(newAddedFiles, newDeletedFiles);
    } else {
      // Allow the same file guid to be "added" multiple times
      // and handle this by only including the file once in the list
      // of files to be uploaded
      const addedFilesWithoutFileGuid = addedFiles.filter(
        (f) => f.fileGuid !== siteFile.fileGuid
      );
      setAddedFiles([...addedFilesWithoutFileGuid, siteFile]);
      updateSiteFiles([...addedFilesWithoutFileGuid, siteFile], deletedFiles);
    }
  };

  const onFileDeleted = (siteFile: SiteFile) => {
    const existingSiteFile = siteFiles.find(
      (f) => f.fileVersionId === siteFile.fileVersionId
    );
    if (existingSiteFile?.fileVersionId) {
      setDeletedFiles([...deletedFiles, existingSiteFile]);
      updateSiteFiles(addedFiles, [...deletedFiles, existingSiteFile]);
    } else {
      const addedFilesWithoutFile = addedFiles.filter(
        (f) => f.fileGuid !== siteFile.fileGuid
      );
      setAddedFiles([...addedFilesWithoutFile]);
      updateSiteFiles([...addedFilesWithoutFile], deletedFiles);
    }

    if (siteFile.fileType === "pvDesignImage") {
      // need to clear the pv design image input value so that they
      // user can upload the same file again if they want
      if (pvDesignFileUploadRef.current) {
        pvDesignFileUploadRef.current.value = "";
      }
    }
  };

  const onSaveClick = (withExtraUpdates?: {
    site?: RecursivePartialSiteAllowStringValues;
    [key: string]: any;
  }) => {
    setSaveDisabled(true);

    if (
      previewSite.commerciallyOperationalDate &&
      previewSite.commerciallyOperationalDate <
        getTodayInTimeZone(site.timeZone) &&
      !previewSite.operationalStatus?.startsWith("operational")
    ) {
      setSaveWarnings([
        "You've set an operational date in the past on a site that is not operational.",
      ]);
      setWithExtraUpdatesFromModal(withExtraUpdates);
      return;
    }

    saveSite(withExtraUpdates);
  };

  const prepareSavePayload = (withExtraUpdates?: {
    site?: RecursivePartialSiteAllowStringValues;
    [key: string]: any;
  }) => {
    const originalBuildingKeys = originalBuildings.map((b) => b.id);
    const newBuildingKeys = newBuildings.map((b) => b.key);

    const addedBuildings: BuildingWithKey[] = [];
    const deletedBuildings: Building[] = [];

    newBuildings.forEach((b) => {
      if (b.customised) {
        addedBuildings.push(b);
        return;
      }
      if (!originalBuildingKeys.includes(b.key)) addedBuildings.push(b);
    });

    originalBuildings.forEach((b) => {
      if (!newBuildingKeys.includes(b.id)) deletedBuildings.push(b);
    });

    const newOwnershipsToSend = [
      ...Object.values(newOwnerships),
      ...Object.values(editedOwnerships),
    ];

    const update = {
      site: {
        ...siteUpdates,
      },
      ...(addedBuildings.length > 0 ? { addedBuildings } : {}),
      ...(deletedBuildings.length > 0 ? { deletedBuildings } : {}),
      ...(addedFiles.length > 0 ? { addedFiles } : {}),
      ...(deletedFiles.length > 0 ? { deletedFiles } : {}),
      ...(newOwnershipsToSend.length > 0
        ? { newOwnerships: newOwnershipsToSend }
        : {}),
      ...(deletedOwnerships.length > 0
        ? { deletedOwnerships: deletedOwnerships }
        : {}),
    };

    return deepmerge(update, withExtraUpdates ?? {}, {
      arrayMerge: overwriteMerge,
    });
  };

  const saveSite = (withExtraUpdates?: {
    site?: RecursivePartialSiteAllowStringValues;
    [key: string]: any;
  }) => {
    const updateWithExtras = prepareSavePayload(withExtraUpdates);
    setSaveInProgress(true);
    setAlertStyle("secondary");
    if (updateWithExtras.site.locked) {
      setAlertMessage("Locking site...");
    } else {
      setAlertMessage("Saving site...");
    }
    updateSite.mutate(updateWithExtras);
  };

  if (updateSite.isSuccess) {
    setSaveDisabled(false);
    setSaveInProgress(false);
    updateSite.reset();
    // Trigger a reload via window.location.href as Firefox was ignoring window.location.reload() when using beforeunload
    // eslint-disable-next-line no-self-assign
    window.location.href = window.location.href;
  }

  if (updateSite.isError) {
    setSaveDisabled(false);
    setSaveInProgress(false);
    const error = updateSite.error as any;
    const errorMessage = error.response?.data?.message || error.message;
    setAlertMessage(`Error updating site - ${errorMessage}`);
    setAlertStyle("danger");
    alert(`Error updating site - ${errorMessage}`);
    updateSite.reset();
  }

  const onDeleteSiteClick = () => {
    if (siteId) {
      deleteSite.mutate(siteId);
    }
  };

  if (deleteSite.isSuccess) {
    deleteSite.reset();
    const ownerships = site.siteOwnerships;
    let target: SiteOwnership | undefined;
    if (ownerships.length === 1) {
      target = ownerships[0];
    } else {
      const nonTest = ownerships.filter((o) => !o.isTest);
      const candidates = nonTest.length > 0 ? nonTest : ownerships;
      target =
        candidates.find((o) => o.ownership === "ownerOccupier") ??
        candidates.find((o) => o.ownership === "landlord") ??
        candidates.find((o) => o.ownership === "tenant");
    }
    navigate(target ? `/organisation/${target.orgID}/edit` : "/");
  }

  if (deleteSite.isError) {
    const error = deleteSite.error as any;
    const errorMessage = error.response?.data?.message || error.message;
    alert(`Error deleting site - ${errorMessage}`);
    deleteSite.reset();
  }

  document.title = `Site: ${site.name}`;

  const selectPvDesign = (installedCapacity: number) => {
    const update = { generation: {} };
    // iterate over the generation key, values in previewSite.generation
    for (const [genKey, genValue] of Object.entries(
      previewSite.generation || {}
    )) {
      if (genValue && typeof genValue === "object") {
        update.generation[genKey] = {
          installedCapacity: 0,
        };
      }
    }
    update.generation[0].installedCapacity = installedCapacity;
    setSiteState({
      ...update,
      pvDesignSystemLastUpdatedAt: Math.floor(Date.now() / 1000),
      pvDesignSystemLastUpdatedBy: user?.email,
    });
  };

  const deselectPvDesign = () => {
    setSiteState({
      pvDesignSystemLastUpdatedAt: null as unknown as undefined,
      pvDesignSystemLastUpdatedBy: null as unknown as undefined,
    });
  };

  const handlePreviewChangelog = async () => {
    const update = prepareSavePayload();

    try {
      const response = await previewSiteLog.mutateAsync(update);
      if (response.log) {
        setChangelogPreview(response.log);
      } else {
        setChangelogPreview({
          siteID: siteId || "",
          time: Math.floor(Date.now() / 1000),
          sortTime: Math.floor(Date.now() / 1000),
          updatedBy: user?.email || "",
          userChange: {},
          recalculated: {},
        });
      }
      setShowChangelogModal(true);
    } catch (error) {
      console.error("Error previewing changelog:", error);
    }
  };

  return (
    <>
      <SiteCalculationsProvider
        site={previewSite as unknown as RecursivePartial<Site>}
        originalSite={site}
        setSaveDisabled={setSaveDisabled}
        app="admin"
      >
        <SiteEditView
          site={previewSite}
          setSite={setSiteState}
          pendingUpdateSite={pendingUpdateSite}
          setPendingUpdateSite={setPendingUpdateSite}
          hasUnsavedBuildings={hasUnsavedBuildings}
          setHasUnsavedBuildings={setHasUnsavedBuildings}
          onSaveClick={onSaveClick}
          setBuildingsForPreview={setBuildingsForPreview}
          ownerships={previewSite.siteOwnerships || []}
          onOwnerAdded={onOwnerAdded}
          isOwnerOccupied={isOwnerOccupied}
          saveDisabled={saveDisabled}
          setSaveDisabled={setSaveDisabled}
          saveWarnings={saveWarnings}
          setSaveWarnings={setSaveWarnings}
          siteStatusMessage={siteStatusMessage}
          alertStyle={alertStyle}
          setAlertStyle={setAlertStyle}
          alertMessage={alertMessage}
          setAlertMessage={setAlertMessage}
          mode="edit"
          siteUpdates={siteUpdates}
          siteBuildings={site.buildings}
          selectPvDesign={selectPvDesign}
          pvDesignInstalledCapacity={pvDesignInstalledCapacity}
          setPvDesignInstalledCapacity={setPvDesignInstalledCapacity}
          deselectPvDesign={deselectPvDesign}
          onOwnerRemoved={onOwnerRemoved}
          onOwnerEdited={onOwnerEdited}
          siteFiles={siteFiles}
          onFileAdded={onFileAdded}
          onFileDeleted={onFileDeleted}
          onDeleteSiteClick={onDeleteSiteClick}
          pvDesignUploadRef={pvDesignFileUploadRef}
          userEmail={user?.email || ""}
          handlePreviewChangelog={handlePreviewChangelog}
          saveSite={saveSite}
          withExtraUpdatesFromModal={withExtraUpdatesFromModal}
          navigationBlocker={
            navigationBlocker.state !== "unblocked"
              ? {
                  state: navigationBlocker.state,
                  reset: () => navigationBlocker.reset?.(),
                  proceed: () => navigationBlocker.proceed?.(),
                }
              : undefined
          }
        />
        {showChangelogModal && changelogPreview && (
          <ChangelogModal
            logItem={changelogPreview}
            siteId={siteId!}
            lossFactorsByKey={LOSS_FACTORS_BY_KEY}
            onClose={() => setShowChangelogModal(false)}
          />
        )}
      </SiteCalculationsProvider>
    </>
  );
};

export default SiteEdit;
